// src/components/manager-v2/teamTaskbox/CreateTaskModal.js
import React, { useState, useEffect } from 'react';
import { X, Plus, Upload, Users, Users2, Calendar, Clock, Flag, Tag, Search, Building2 } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { api } from '../../../services/api';
import { useEmployees } from '../../../contexts/EmployeeContext';

const CreateTaskModal = ({ onClose, onSubmit }) => {
    const { user } = useAuth();
    const { employees: allEmployees } = useEmployees();
    const [loading, setLoading] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [teams, setTeams] = useState([]);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        assign_to_type: 'employee',
        assigned_employee_ids: [],
        assigned_department_id: null,
        priority: 'scheduled',
        deadline: '',
        due_date: '',
        estimated_hours: '',
        category: '',
        tags: []
    });

    const [errors, setErrors] = useState({});

    // Fetch employees and departments and generate teams
    useEffect(() => {
        setupData();
    }, [allEmployees]);

    const setupData = () => {
        // Use employees from EmployeeContext
        if (allEmployees.length > 0) {
            setEmployees(allEmployees);
        }

        // Find all employees who have direct reports (they are reporting managers)
        const employeesWithDirectReports = allEmployees.filter(manager => {
            const hasDirectReports = allEmployees.some(emp => emp.reporting_manager_id === manager.id);
            return hasDirectReports;
        });

        const generatedTeams = employeesWithDirectReports.map(manager => {
            const directReports = allEmployees.filter(emp => emp.reporting_manager_id === manager.id);
            return {
                id: manager.id,
                name: `${manager.first_name} ${manager.last_name} and Team`,
                managerId: manager.id,
                managerName: `${manager.first_name} ${manager.last_name}`,
                directReports: directReports,
                teamSize: directReports.length + 1 // Include manager in team size
            };
        });

        console.log('🔍 Generated teams from reporting managers:', generatedTeams);
        setTeams(generatedTeams);

        // Try to fetch departments from API if available
        fetchDepartments();
    };

    const fetchDepartments = async () => {
        try {
            const deptResponse = await api.api.get('/api/departments');
            if (deptResponse.data) {
                setDepartments(Array.isArray(deptResponse.data) ? deptResponse.data : deptResponse.data.departments || []);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
            // Fallback: create departments from employee data
            const uniqueDepartments = [...new Set(allEmployees.map(emp => emp.department).filter(Boolean))];
            const departmentOptions = uniqueDepartments.map((dept, index) => ({
                id: `dept_${index}`,
                name: dept
            }));
            setDepartments(departmentOptions);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleEmployeeToggle = (employeeId) => {
        setFormData(prev => {
            const isSelected = prev.assigned_employee_ids.includes(employeeId);
            return {
                ...prev,
                assigned_employee_ids: isSelected
                    ? prev.assigned_employee_ids.filter(id => id !== employeeId)
                    : [...prev.assigned_employee_ids, employeeId]
            };
        });
    };

    // Handle department selection with auto-selecting department employees
    const handleDepartmentSelect = (departmentId) => {
        const departmentEmployees = employees.filter(emp => emp.department === departments.find(d => d.id === departmentId)?.name);
        const departmentEmployeeIds = departmentEmployees.map(emp => emp.id);

        setFormData(prev => ({
            ...prev,
            assigned_department_id: departmentId,
            assigned_employee_ids: departmentEmployeeIds
        }));
    };

    // Handle team selection with auto-selecting manager and direct reports
    const handleTeamSelect = (teamId) => {
        const selectedTeam = teams.find(team => team.id === teamId);
        if (!selectedTeam) return;

        const teamMembers = [
            selectedTeam.managerId,
            ...selectedTeam.directReports.map(emp => emp.id)
        ];

        setFormData(prev => ({
            ...prev,
            assigned_team_id: teamId,
            assigned_employee_ids: teamMembers
        }));
    };

    // Filter employees based on search term
    const filteredEmployees = employees.filter(emp =>
        emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const validate = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (formData.assign_to_type === 'employee' && formData.assigned_employee_ids.length === 0) {
            newErrors.assigned_employee_ids = 'Please select at least one employee';
        }

        if (formData.assign_to_type === 'department' && !formData.assigned_department_id) {
            newErrors.assigned_department_id = 'Please select a department';
        }

        if (formData.assign_to_type === 'team' && !formData.assigned_team_id) {
            newErrors.assigned_team_id = 'Please select a team';
        }

        if (!formData.priority) {
            newErrors.priority = 'Priority is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setLoading(true);

        try {
            const cleanedData = {
                title: formData.title,
                // Send description as plain text
                description: formData.description || '',
                assign_to_type: formData.assign_to_type,
                assigned_employee_ids: formData.assigned_employee_ids,
                priority: formData.priority,
                deadline: formData.deadline || null,
                due_date: formData.due_date || null,
                estimated_hours: formData.estimated_hours || null,
                category: formData.category || null,
                tags: formData.tags || []
            };

            // Add department or team ID if selected
            if (formData.assign_to_type === 'department' && formData.assigned_department_id) {
                cleanedData.assigned_department_id = formData.assigned_department_id;
            }
            if (formData.assign_to_type === 'team' && formData.assigned_team_id) {
                cleanedData.assigned_team_id = formData.assigned_team_id;
            }

            // Temporary workaround: Set assigned_to to first employee if multiple
            if (formData.assigned_employee_ids.length > 0) {
                cleanedData.assigned_to = formData.assigned_employee_ids[0];
            }

            console.log('📝 Submitting cleaned task data:', cleanedData);
            await onSubmit(cleanedData);
        } catch (error) {
            console.error('Error submitting form:', error);

            // Extract detailed error message
            let errorMessage = 'Failed to create task. Please try again.';

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }

            // Add validation errors if present
            if (error.response?.data?.errors) {
                const validationErrors = Object.values(error.response.data.errors).join(', ');
                errorMessage += `\n\nValidation errors:\n${validationErrors}`;
            }

            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const priorityOptions = [
        { value: 'immediate', label: '🔴 Immediate', color: 'text-red-600' },
        { value: 'urgent', label: '🟠 Urgent', color: 'text-orange-600' },
        { value: 'next_in_line', label: '🟡 Next in Line', color: 'text-yellow-600' },
        { value: 'scheduled', label: '🟢 Scheduled', color: 'text-green-600' },
        { value: 'deferred', label: '⚪ Deferred', color: 'text-gray-600' }
    ];

    const categoryOptions = [
        'Project',
        'Administrative',
        'Training',
        'Event',
        'Research',
        'Development',
        'Marketing',
        'Support',
        'Other'
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Create New Team Task
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
                    <div className="p-6 space-y-6">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Task Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                                    errors.title ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                }`}
                                placeholder="Enter task title..."
                            />
                            {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={4}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                placeholder="Describe the task in detail..."
                            />
                        </div>

                        {/* Assignment Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Assign To *
                            </label>
                            <div className="flex gap-4 mb-4">
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, assign_to_type: 'employee' }))}
                                    className={`flex-1 py-2 px-4 rounded-lg border-2 transition-colors ${
                                        formData.assign_to_type === 'employee'
                                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                                    }`}
                                >
                                    <Users className="w-5 h-5 inline mr-2" />
                                    Employees
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, assign_to_type: 'department' }))}
                                    className={`flex-1 py-2 px-4 rounded-lg border-2 transition-colors ${
                                        formData.assign_to_type === 'department'
                                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                                    }`}
                                >
                                    Department
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, assign_to_type: 'team' }))}
                                    className={`flex-1 py-2 px-4 rounded-lg border-2 transition-colors ${
                                        formData.assign_to_type === 'team'
                                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                                    }`}
                                >
                                    Team
                                </button>
                            </div>

                            {/* Employee Selection */}
                            {formData.assign_to_type === 'employee' && (
                                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                                    {/* Search Bar */}
                                    <div className="relative mb-4">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search employees..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                        />
                                    </div>

                                    {/* Employee List */}
                                    <div className="max-h-48 overflow-y-auto">
                                        {filteredEmployees.length === 0 ? (
                                            <p className="text-gray-500 text-center py-4">
                                                {searchTerm ? 'No employees found matching your search' : 'No employees found'}
                                            </p>
                                        ) : (
                                            <div className="space-y-2">
                                                {filteredEmployees.map(emp => (
                                                    <label key={emp.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.assigned_employee_ids.includes(emp.id)}
                                                            onChange={() => handleEmployeeToggle(emp.id)}
                                                            className="w-4 h-4 text-blue-600"
                                                        />
                                                        <div className="flex items-center gap-2 flex-1">
                                                            {emp.profile_picture_url && (
                                                                <img
                                                                    src={emp.profile_picture_url}
                                                                    alt={`${emp.first_name} ${emp.last_name}`}
                                                                    className="w-8 h-8 rounded-full"
                                                                />
                                                            )}
                                                            <div className="flex-1">
                                                                <span className="text-sm text-gray-900 dark:text-white">
                                                                    {emp.first_name} {emp.last_name}
                                                                </span>
                                                                {emp.designation && (
                                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                        {emp.designation}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {errors.assigned_employee_ids && (
                                        <p className="text-red-500 text-sm mt-2">{errors.assigned_employee_ids}</p>
                                    )}
                                    <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                                        Selected: {formData.assigned_employee_ids.length} employee(s)
                                    </div>
                                </div>
                            )}

                            {/* Department Selection */}
                            {formData.assign_to_type === 'department' && (
                                <div>
                                    <select
                                        value={formData.assigned_department_id || ''}
                                        onChange={(e) => handleDepartmentSelect(e.target.value)}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                                            errors.assigned_department_id ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                        }`}
                                    >
                                        <option value="">Select Department</option>
                                        {departments.map(dept => (
                                            <option key={dept.id} value={dept.id}>
                                                {dept.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.assigned_department_id && (
                                        <p className="text-red-500 text-sm mt-1">{errors.assigned_department_id}</p>
                                    )}
                                    {formData.assigned_department_id && (
                                        <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                            {formData.assigned_employee_ids.length} employee(s) will be assigned from this department
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Team Selection */}
                            {formData.assign_to_type === 'team' && (
                                <div>
                                    <select
                                        value={formData.assigned_team_id || ''}
                                        onChange={(e) => handleTeamSelect(e.target.value)}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                                            errors.assigned_team_id ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                        }`}
                                    >
                                        <option value="">Select Team</option>
                                        {teams.map(team => (
                                            <option key={team.id} value={team.id}>
                                                {team.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.assigned_team_id && (
                                        <p className="text-red-500 text-sm mt-1">{errors.assigned_team_id}</p>
                                    )}
                                    {formData.assigned_team_id && (
                                        <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                            {formData.assigned_employee_ids.length} team member(s) will be assigned
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Priority and Category Row */}
                        <div className="grid grid-cols-2 gap-4">
                            {/* Priority */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    <Flag className="w-4 h-4 inline mr-1" />
                                    Priority *
                                </label>
                                <select
                                    name="priority"
                                    value={formData.priority}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                >
                                    {priorityOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Category */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    <Tag className="w-4 h-4 inline mr-1" />
                                    Category
                                </label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">Select Category</option>
                                    {categoryOptions.map(cat => (
                                        <option key={cat} value={cat.toLowerCase()}>
                                            {cat}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Deadline and Estimated Hours Row */}
                        <div className="grid grid-cols-2 gap-4">
                            {/* Deadline */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    <Calendar className="w-4 h-4 inline mr-1" />
                                    Deadline
                                </label>
                                <input
                                    type="date"
                                    name="deadline"
                                    value={formData.deadline}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* Estimated Hours */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    <Clock className="w-4 h-4 inline mr-1" />
                                    Estimated Hours
                                </label>
                                <input
                                    type="number"
                                    name="estimated_hours"
                                    value={formData.estimated_hours}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.5"
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    placeholder="e.g., 8"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Plus className="w-5 h-5" />
                                    Create Task
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateTaskModal;